import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faGrid2 } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router';

import { catchAll } from '@sorare/routing';
import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Cards } from '@sorare/core/src/atoms/icons/Cards';
import { Collection } from '@sorare/core/src/atoms/icons/Collection';
import { RevealCard } from '@sorare/core/src/atoms/icons/RevealCard';
import { Vault } from '@sorare/core/src/atoms/icons/Vault';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { buildFilterQuery } from '@sorare/core/src/components/search/InstantSearch/utils';
import {
  ANY_SPORT_MY_CARDS_CARDS,
  ANY_SPORT_MY_CARDS_CARDS_TYPE,
  ANY_SPORT_MY_CARDS_COLLECTIONS,
  ANY_SPORT_MY_CARDS_COLLECTIONS_TYPE,
  ANY_SPORT_MY_CARDS_GALLERY,
  ANY_SPORT_MY_CARDS_REVEALABLE_CARDS,
  ANY_SPORT_MY_CARDS_VAULT,
  ANY_SPORT_MY_CARDS_VAULT_TYPE,
  ANY_SPORT_MY_CLUB_SLUG_CARDS_TYPE,
  ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS,
  ANY_SPORT_MY_CLUB_SLUG_VAULT_TYPE,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { fantasy, navLabels } from '@sorare/core/src/lib/glossary';
import { SegmentButton } from '@sorare/marketplace/src/searchCards/Segment/ui';

import { MyCardsLayoutContext } from '#src/layouts/my-cards/MyCardsLayout/MyCardsLayoutContext.tsx';

import type { CollectionNavigationSwitch_user } from './__generated__/index.graphql.ts';

type Props = {
  rarity: Nullable<Rarity>;
  user: Nullable<CollectionNavigationSwitch_user>;
};

export const CollectionNavigationSwitch = ({ rarity, user }: Props) => {
  const { pathname } = useLocation();

  const { generateSportContextPath, matchAnySportPath } = useSportContext();

  const isCollection =
    pathname.includes(ANY_SPORT_MY_CARDS_COLLECTIONS) ||
    matchAnySportPath(ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS, pathname) ||
    matchAnySportPath(catchAll(ANY_SPORT_MY_CARDS_GALLERY), pathname);
  const isVault =
    pathname.includes(ANY_SPORT_MY_CARDS_VAULT) ||
    matchAnySportPath(ANY_SPORT_MY_CLUB_SLUG_VAULT_TYPE, pathname);
  const isRevealCards = pathname.includes(ANY_SPORT_MY_CARDS_REVEALABLE_CARDS);
  const isCommon = rarity === Rarity.common;

  const { defaultRarity } = useContext(MyCardsLayoutContext);
  const { isMe } = useCurrentUserContext();
  const myCards = isMe(user);

  const isTabletAndAbove = useIsTabletAndAbove();
  const { formatMessage } = useIntl();

  if (!user) {
    return null;
  }
  const { cardCounts, anyUserSportProfile } = user;
  const cardsCountByRarity = {
    [Rarity.common]: cardCounts?.common || 0,
    [Rarity.limited]: cardCounts?.limited || 0,
    [Rarity.rare]: cardCounts?.rare || 0,
    [Rarity.super_rare]: cardCounts?.superRare || 0,
    [Rarity.unique]: cardCounts?.unique || 0,
    [Rarity.custom_series]: cardCounts?.customSeries || 0,
  };

  const cardCount = rarity && cardsCountByRarity?.[rarity];
  const rarityPath = (cardCount && rarity) || defaultRarity;
  const vaultType =
    // we don't have common Vault so switch to Limited upon visiting Vault
    isCommon ? Rarity.limited : rarity || Rarity.limited;

  const { blueprintCardsCount = 0 } = anyUserSportProfile || {};
  const showRevealableCardsTab =
    myCards && blueprintCardsCount > 0 && !isCommon;
  const myCardsTypePath = myCards
    ? generateSportContextPath(ANY_SPORT_MY_CARDS_CARDS_TYPE, {
        params: {
          type: rarityPath,
        },
      })
    : generateSportContextPath(ANY_SPORT_MY_CLUB_SLUG_CARDS_TYPE, {
        params: {
          type: rarityPath,
          slug: user.slug,
        },
      });

  const collectionsTypePath = myCards
    ? generateSportContextPath(ANY_SPORT_MY_CARDS_COLLECTIONS_TYPE, {
        params: {
          type: rarity || Rarity.limited,
        },
      })
    : generateSportContextPath(ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS, {
        params: {
          slug: user.slug,
        },
      });
  const vaultTypePath = myCards
    ? generateSportContextPath(ANY_SPORT_MY_CARDS_VAULT_TYPE, {
        params: {
          type: vaultType,
        },
      })
    : generateSportContextPath(ANY_SPORT_MY_CLUB_SLUG_VAULT_TYPE, {
        params: {
          slug: user.slug,
          type: vaultType,
        },
      });

  const revealableTypePath = `${generateSportContextPath(
    ANY_SPORT_MY_CARDS_REVEALABLE_CARDS
  )}?${buildFilterQuery({
    [SEARCH_PARAMS.MENU_RARITY]: rarity || Rarity.limited,
  })}`;

  if (isTabletAndAbove) {
    return (
      <Horizontal>
        <SegmentButton
          to={myCardsTypePath}
          className={classNames({
            checked: !isCollection && !isVault && !isRevealCards,
          })}
        >
          <FontAwesomeIcon icon={faGrid2} />
          <BodyM bold>
            <FormattedMessage {...navLabels.gallery} />
          </BodyM>
        </SegmentButton>
        <SegmentButton
          to={collectionsTypePath}
          className={classNames({ checked: isCollection })}
        >
          <Collection />
          <BodyM bold>
            <FormattedMessage {...navLabels.collections} />
          </BodyM>
        </SegmentButton>
        {!isCommon && (
          <SegmentButton
            as={isCommon ? 'button' : undefined}
            to={vaultTypePath}
            className={classNames({
              checked: isVault,
              disabled: isCommon,
            })}
          >
            <Vault size={2} />
            <BodyM bold>
              <FormattedMessage {...navLabels.vault} />
            </BodyM>
          </SegmentButton>
        )}
        {!!showRevealableCardsTab && (
          <SegmentButton
            to={revealableTypePath}
            className={classNames({ checked: isRevealCards })}
          >
            <RevealCard size={2} />
            <BodyM bold>
              <FormattedMessage {...fantasy.reveal} />
            </BodyM>
          </SegmentButton>
        )}
      </Horizontal>
    );
  }

  return (
    <TabBar forceMobileLayout compact>
      <Tab
        to={collectionsTypePath}
        matches={[
          catchAll(generateSportContextPath(ANY_SPORT_MY_CARDS_COLLECTIONS)),
          catchAll(generateSportContextPath(ANY_SPORT_MY_CARDS_GALLERY)),
        ]}
      >
        <Collection title={formatMessage(navLabels.collections)} />
      </Tab>
      <Tab
        to={myCardsTypePath}
        matches={[catchAll(generateSportContextPath(ANY_SPORT_MY_CARDS_CARDS))]}
      >
        <Cards />
      </Tab>

      {!isCommon && (
        <Tab
          to={vaultTypePath}
          matches={[
            catchAll(generateSportContextPath(ANY_SPORT_MY_CARDS_VAULT)),
          ]}
        >
          <Vault size={1.5} title={formatMessage(navLabels.vault)} />
        </Tab>
      )}
      {!!showRevealableCardsTab && (
        <Tab to={revealableTypePath}>
          <RevealCard size={1.5} title={formatMessage(fantasy.reveal)} />
        </Tab>
      )}
    </TabBar>
  );
};

CollectionNavigationSwitch.fragments = {
  user: gql`
    fragment CollectionNavigationSwitch_user on PublicUserInfoInterface {
      slug
      cardCounts(sport: $sport) {
        common
        limited
        rare
        superRare
        unique
        customSeries
      }
      anyUserSportProfile(sport: $sport) {
        id
        blueprintCardsCount
      }
    }
  ` as TypedDocumentNode<CollectionNavigationSwitch_user>,
};
